import React, { FC, useEffect, useRef } from 'react';

import Chart from 'features/drilldown/cashflows/modelingChart/Chart';
import MonthlyTable from 'features/drilldown/cashflows/MonthlyTable';
import SummaryTable from 'features/drilldown/cashflows/SummaryTable';

import { usePortfolioContext } from 'features/pages/portfolio/PortfolioContext';

import { useSelectedPoolCashflowValue } from './CashFlows';
import { CashFlowsValidValues } from './configurations/cashflow-inputs';
import { ChartSkeletonLoader } from './modelingChart/ChartSkeletonLoader';

import { PortfolioSummaryNumericSnapshot } from 'features/pages/portfolio/PortfolioSummary';
import { PoolCashflowPerfSummary } from 'features/pages/portfolio/CashFlowModeling/getSelectedPoolCashflow_gql';
type CashFlowDataDisplayProps = {
  cashflowData: PoolCashflowPerfSummary | undefined;
  userInputs: CashFlowsValidValues;
  selectedPoolId?: string;
  selectedPoolCashflow?: (
    listingId: string,
    inputValues: CashFlowsValidValues,
  ) => useSelectedPoolCashflowValue;
  loading: boolean;
  /** When the `monthlyCashFlows` series is a merged past+projection timeline,
   *  pass the past-row count so the chart renders an "Actual | Projected"
   *  divider and the monthly table labels each row's Period. Opt-in; when
   *  omitted both surfaces render unchanged. */
  pastMonthCount?: number;
};

const CashFlowsDataDisplay: FC<CashFlowDataDisplayProps> = ({
  cashflowData,
  selectedPoolId,
  selectedPoolCashflow,
  loading,
  pastMonthCount,
}) => {
  const { setTotalFilteredPoolData } = usePortfolioContext();
  const data = cashflowData;
  const monthlyCashFlow = data?.cashFlows?.monthlyCashFlows;
  const totals = data?.cashFlows?.totals;
  const filteredPoolDataRef = useRef<PortfolioSummaryNumericSnapshot | null>(null);

  if (selectedPoolCashflow && selectedPoolId) {
    if (!loading && data?.cashFlows) {
      const totalFiltered: PortfolioSummaryNumericSnapshot = {
        averageAge: data.averageAge,
        averageMaturity: data.averageMaturity,
        dti: data.dti,
        fico: data.fico,
        ltv: data.ltv,
        netCoupon: data.netCoupon,
        unpaidBalance: data.unpaidBalance,
        loanCount: data.loanCount,
        averageBalance: data.averageBalance,
        originalBalance: data.originalBalance,
        wa_servicing_rate: data.wa_servicing_rate,
        minDscr: data.minDscr,
        maxDscr: data.maxDscr,
        avgDscr: data.avgDscr,
        dscrCount: data.dscrCount,
        nonQmCount: data.nonQmCount,
        dscrPct: data.dscrPct,
        nonQmPct: data.nonQmPct,
        assetUtilCount: data.assetUtilCount,
        assetUtilPct: data.assetUtilPct,
      };

      filteredPoolDataRef.current = totalFiltered;
    }
  }

  useEffect(() => {
    if (!loading) {
      setTotalFilteredPoolData(filteredPoolDataRef.current);
    }

    if (!selectedPoolId) {
      setTotalFilteredPoolData(null);
    }
  }, [loading, selectedPoolId, setTotalFilteredPoolData]);

  return (
    <>
      <div className="mx-5 my-0 font-heebo text-lg font-normal text-gray-100">
        Cashflow Projections
      </div>
      <div className="mt-5 h-[318px] w-full font-heebo text-lg font-normal">
        {loading ? (
          <ChartSkeletonLoader />
        ) : (
          <Chart cashFlowMonths={monthlyCashFlow} pastMonthCount={pastMonthCount} />
        )}
      </div>
      <div className="mt-5 flex flex-nowrap gap-x-3">
        <div>
          <div className="mx-0 my-5 font-heebo text-lg font-normal text-gray-100">
            Cashflow Matrix
          </div>
          <SummaryTable cashFlowTotals={totals ?? null} performanceSummary={data ?? null} />
        </div>
        {monthlyCashFlow != null && (
          <MonthlyTable monthlyCashFlows={monthlyCashFlow} pastMonthCount={pastMonthCount} />
        )}
      </div>
    </>
  );
};

export default CashFlowsDataDisplay;
